import * as yup from 'yup';

import { Flow } from 'vivaa-api';

export const FlowKind = Flow.FlowKind;
export type FlowKind = Flow.FlowKind;

const FlowFieldConstants = {
  name: {
    minLength: 1,
    // We have flow names that are 200 characters long.
    maxLength: 250,
  },
  description: {
    minLength: 8,
    maxLength: 250,
  },
};

const SkillFieldConstants = {
  name: {
    minLength: 8,
    maxLength: 60,
  },
  description: {
    minLength: 8,
    maxLength: 250,
  },
};

interface FlowKindContent {
  name: string;
  lowerCaseName: string;
  metadataFields: {
    name: {
      label: string;
      helperText: string;
      yupSchema: yup.StringSchema;
    };
    description: {
      label: string;
      helperText: string | undefined;
      yupSchema: yup.StringSchema;
    };
    notes: {
      label: string;
      helperText: string | undefined;
      yupSchema: yup.StringSchema;
    };
    documentationMarkdown: {
      label: string;
      helperText: string | undefined;
      yupSchema: yup.StringSchema;
    };
    customTags: {
      yupSchema: yup.AnySchema;
    };
    useCaseTag: {
      yupSchema: yup.AnySchema;
    };
    flowTypeTag: {
      yupSchema: yup.AnySchema;
    };
    supportedEhrTags: {
      yupSchema: yup.AnySchema;
    };
  };
}

export const FlowKindContent: Record<FlowKind, FlowKindContent> = {
  [Flow.FlowKind.Base]: {
    name: 'Flow',
    lowerCaseName: 'flow',
    metadataFields: {
      name: {
        label: 'Flow name*',
        helperText: `Name must be between ${FlowFieldConstants.name.minLength} and ${FlowFieldConstants.name.maxLength} characters`,
        yupSchema: yup
          .string()
          .required('Required')
          .min(
            FlowFieldConstants.name.minLength,
            `Name must be at least ${FlowFieldConstants.name.minLength} characters`
          )
          .max(
            FlowFieldConstants.name.maxLength,
            `Name cannot exceed ${FlowFieldConstants.name.maxLength} characters`
          ),
      },
      description: {
        label: 'Flow description',
        helperText: undefined,
        yupSchema: yup
          .string()
          .notRequired()
          .max(
            FlowFieldConstants.description.maxLength,
            `Description cannot exceed ${FlowFieldConstants.description.maxLength} characters`
          ),
      },
      notes: {
        label: 'Additional comments for reviewer',
        helperText: undefined,
        yupSchema: yup.string().notRequired(),
      },
      documentationMarkdown: {
        label: 'Documentation',
        helperText: undefined,
        yupSchema: yup.string().notRequired(),
      },
      customTags: {
        yupSchema: yup.array(yup.string()).nullable().optional(),
      },
      useCaseTag: {
        yupSchema: yup.string().nullable().optional(),
      },
      flowTypeTag: {
        yupSchema: yup.string().nullable().optional(),
      },
      supportedEhrTags: {
        yupSchema: yup.array(yup.string()).nullable().optional(),
      },
    },
  },
  [Flow.FlowKind.Skill]: {
    name: 'Skill',
    lowerCaseName: 'skill',
    metadataFields: {
      name: {
        label: 'Skill name*',
        helperText: `Skill name must be between ${SkillFieldConstants.name.minLength} and ${SkillFieldConstants.name.maxLength} characters`,
        yupSchema: yup
          .string()
          .required('Required')
          .min(
            SkillFieldConstants.name.minLength,
            `Name must be at least ${SkillFieldConstants.name.minLength} characters`
          )
          .max(
            SkillFieldConstants.name.maxLength,
            `Name cannot exceed ${SkillFieldConstants.name.maxLength} characters`
          ),
      },
      description: {
        label: 'Short description for library*',
        helperText: `Description must be between ${SkillFieldConstants.description.minLength} and ${SkillFieldConstants.description.maxLength} characters`,
        yupSchema: yup
          .string()
          .required('Required')
          .min(
            SkillFieldConstants.description.minLength,
            `Description must be at least ${SkillFieldConstants.description.minLength} characters`
          )
          .max(
            SkillFieldConstants.description.maxLength,
            `Description cannot exceed ${SkillFieldConstants.description.maxLength} characters`
          ),
      },
      notes: {
        label: 'Additional comments for reviewer',
        helperText: undefined,
        yupSchema: yup.string().notRequired(),
      },
      documentationMarkdown: {
        label: 'Documentation*',
        helperText: 'Markdown format',
        yupSchema: yup.string().required('Required'),
      },
      customTags: {
        yupSchema: yup.array(yup.string()).nullable().optional(),
      },
      useCaseTag: {
        yupSchema: yup.string().nullable().optional(),
      },
      flowTypeTag: {
        yupSchema: yup.string().nullable().optional(),
      },
      supportedEhrTags: {
        yupSchema: yup.array(yup.string()).nullable().optional(),
      },
    },
  },
  [Flow.FlowKind.FlowTemplate]: {
    name: 'Flow Template',
    lowerCaseName: 'flow template',
    metadataFields: {
      name: {
        label: 'Flow Template name*',
        helperText: `Flow Template name must be between ${FlowFieldConstants.name.minLength} and ${FlowFieldConstants.name.maxLength} characters`,
        yupSchema: yup
          .string()
          .required('Required')
          .min(
            FlowFieldConstants.name.minLength,
            `Name must be at least ${FlowFieldConstants.name.minLength} characters`
          )
          .max(
            FlowFieldConstants.name.maxLength,
            `Name cannot exceed ${FlowFieldConstants.name.maxLength} characters`
          ),
      },
      description: {
        label: 'Short description for library*',
        helperText: `Description must be between ${FlowFieldConstants.description.minLength} and ${FlowFieldConstants.description.maxLength} characters`,
        yupSchema: yup
          .string()
          .required('Required')
          .min(
            FlowFieldConstants.description.minLength,
            `Description must be at least ${FlowFieldConstants.description.minLength} characters`
          )
          .max(
            FlowFieldConstants.description.maxLength,
            `Description cannot exceed ${FlowFieldConstants.description.maxLength} characters`
          ),
      },
      notes: {
        label: 'Additional comments for reviewer',
        helperText: undefined,
        yupSchema: yup.string().notRequired(),
      },
      documentationMarkdown: {
        label: 'Documentation*',
        helperText: 'Markdown format',
        yupSchema: yup.string().required('Required'),
      },
      customTags: {
        yupSchema: yup.array(yup.string()).nullable().optional(),
      },
      useCaseTag: {
        yupSchema: yup.string().nullable().required('Use Case is required'),
      },
      flowTypeTag: {
        yupSchema: yup.string().nullable().required('Flow Type is required'),
      },
      supportedEhrTags: {
        yupSchema: yup
          .array(yup.string().required())
          .min(1, 'Compatible EHRs are required')
          .required('Compatible EHRs are required'),
      },
    },
  },
};

export function getFlowKindContent(flowKind: FlowKind) {
  return FlowKindContent[flowKind];
}
